import { FC, useRef, useState } from 'react';
import { Reactions } from './Reactions';
import { Text } from '../../components/Text';
import { ICaremapNote } from '../../notes/model/NotesClient';
import { createUseStyles } from 'react-jss';
import { IPostInteractionInfoType, PostInteractionModal } from '../PostInteractionModal';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../../notes/model/CaremapNotesModelFactory';
import { DuoPaperPlaneIcon } from '../../theme/Icons';
import { useLongPress } from '@uidotdev/usehooks';
import { isPlatform } from '@ionic/react';

const useStyles = createUseStyles({
    supporterInfoRow: {
        marginLeft: 4,
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    smallText: {
        fontSize: 14
    },

    reactionsRow: {
        marginLeft: 4
    }
});

export type IReactionsRowProps = {
    post: ICaremapNote;
};

export const ReactionsRow: FC<IReactionsRowProps> = ({ post }) => {
    const classes = useStyles();

    const reactionsModel = useInstance(CaremapNotesModelFactory).forActiveCaremap().reactions;

    const [modalInfo, setModalInfo] = useState<{ post: ICaremapNote, type: IPostInteractionInfoType } | undefined>();

    // using state didn't work, my guess is due to how the long press hook is set up, so using a ref works
    const modalType = useRef<{ type: IPostInteractionInfoType }>({type: 'reactions'});

    const showInfoModal = (type: IPostInteractionInfoType) => {
        setModalInfo({ type: modalType.current.type, post: post });
    };

    const isDesktop = isPlatform('desktop');
    const onLongPress = (e: Event) => {
        // on desktop, it's just a 'click' for Sent to show the modal and no events to show the modal on reactions
        if (!isDesktop || modalType.current.type === 'notifications') {
            showInfoModal(modalType.current?.type);
        }
    };

    const longPressAttrs = useLongPress(e => onLongPress(e), {
        threshold: 500,
        onStart: (e: any) => {
            if(e.target.attributes['data-type']?.value === 'post-notification-count') {
                modalType.current.type = 'notifications';
            } else {
                modalType.current.type = 'reactions';
            }

            // for desktop, just make it a 'click'
            if (isDesktop) {
                onLongPress(e);
            }
        }
    });

    const getNotificationCount = () => {
        const cnt = reactionsModel.getNames({note: post, reaction: 'notification-sent:email'}).length;
        return cnt ? <><DuoPaperPlaneIcon color="mediumtext" />&nbsp;{cnt} Supporters</> : '';
    };

    return (
        <>
            <div {...longPressAttrs}>
                <div className={classes.reactionsRow}>
                    <Reactions post={post} />
                </div>
            </div>
            <div
                {...longPressAttrs}
                className={classes.supporterInfoRow}
            >
                <div>&nbsp;</div>
                <div className={classes.smallText}>
                    <Text type="detail" data-type="post-notification-count">
                        {getNotificationCount()}
                    </Text>
                </div>
            </div>

            <PostInteractionModal
                modalInfo={modalInfo}
                closeHandler={() => setModalInfo(undefined)}
            />
        </>
    );
};
